/**
 * Inline Clever publish-state badge for a (lesson, classroom) pair.
 *
 * Fetches the row via `getCleverPublishState`; a 404 from the GET is treated
 * as "not published" rather than an error, so unpublished lessons still get
 * a grey chip next to the title. Colour + label are derived from a local
 * mapping and the component is intentionally dependency-light so it can be
 * dropped into `LessonCard` without a store wire-up.
 */

import { useEffect, useState } from "react";
import { useAuthStore } from "@/store/useAuthStore";
import type { CleverPublishState } from "../types/clever";
import { getCleverPublishState } from "../api/clever";

interface CleverAssignmentStatusBadgeProps {
  classroomId: number | null;
  lessonId: number;
  className?: string;
}

const STATE_LABELS: Record<CleverPublishState, string> = {
  not_published: "Not published",
  publishing: "Publishing…",
  published: "Published",
  updated: "Published",
  deleted: "Unpublished",
  sync_error: "Sync error",
  desynced: "Needs attention",
  not_syncable: "Not linked",
};

// Tailwind classes per state. Kept as a const object so TypeScript catches
// any new CleverPublishState additions at build time.
const STATE_STYLES: Record<CleverPublishState, string> = {
  not_published: "bg-[#EDE8DC] text-[#687368] border-[#DED8CC]",
  publishing: "bg-[#F3EDE2] text-[#92733D] border-[#E2D3B3]",
  published: "bg-[#EEF0E3] text-[#28543D] border-[#D6CEBF]",
  updated: "bg-[#EEF0E3] text-[#28543D] border-[#D6CEBF]",
  deleted: "bg-[#EDE8DC] text-[#687368] border-[#DED8CC]",
  sync_error: "bg-red-50 text-red-700 border-red-200",
  desynced: "bg-amber-50 text-amber-700 border-amber-200",
  not_syncable: "bg-[#EDE8DC] text-[#687368] border-[#DED8CC]",
};

const CleverAssignmentStatusBadge = ({
  classroomId,
  lessonId,
  className,
}: CleverAssignmentStatusBadgeProps) => {
  const [state, setState] = useState<CleverPublishState | null>(null);
  const [loaded, setLoaded] = useState(false);
  const cleverEnabledForTeacher = useAuthStore(
    (store) => store.user?.config?.enableCleverLmsIntegration === true,
  );

  useEffect(() => {
    if (!cleverEnabledForTeacher) {
      setState(null);
      setLoaded(true);
      return;
    }
    // A null classroomId means the lesson isn't tied to a specific classroom
    // in this view (e.g., All Lessons). Skip the fetch rather than guessing.
    if (classroomId == null) {
      setLoaded(true);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const row = await getCleverPublishState(classroomId, lessonId);
        if (cancelled) return;
        setState(
          row ? (row.publish_state as CleverPublishState) : "not_published",
        );
      } catch {
        // Intentionally swallow: the badge is informational. A failed fetch
        // renders nothing rather than a scary error chip.
        if (!cancelled) setState(null);
      } finally {
        if (!cancelled) setLoaded(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [classroomId, cleverEnabledForTeacher, lessonId]);

  if (!loaded || state == null) {
    return null;
  }

  const label = STATE_LABELS[state];
  const styles = STATE_STYLES[state];

  return (
    <span
      role="status"
      aria-label={`Clever publish status: ${label}`}
      className={[
        "inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border whitespace-nowrap",
        styles,
        className ?? "",
      ]
        .filter(Boolean)
        .join(" ")}
    >
      {label}
    </span>
  );
};

export default CleverAssignmentStatusBadge;
